import { z } from 'zod';

import { type DraftIssueSlice } from '../../state/draft-issue-slice';
import { type selectDraftIssueWithStore } from '../../state/store-provider';
import { storeValidationSchema } from '../store/store';
import {
  type DynamicFieldValues,
  type Field,
  createFieldSchema,
} from './field';
import { getFormFields } from './field-registry';
import type { DraftIssue, DraftIssueValidationResult } from './issue';
import {
  attachmentArraySchema,
  categoryValidationSchema,
  descriptionSchema,
  draftIssueSchema,
} from './issue';

export type SchemaErrors = {
  [K in keyof DraftIssue]?: string[];
} & {
  customFields?: Record<string, string[]>;
};

type ResolvedSelections = Pick<DraftIssue, 'area' | 'department' | 'category'>;

type ValidatableDraftIssue = DraftIssueSlice & Partial<ResolvedSelections>;

export const validateDraftIssueWithZod = (
  draftIssue: Partial<ValidatableDraftIssue>
): DraftIssueValidationResult => {
  const result = draftIssueSchema.safeParse(draftIssue);
  if (!result.success) {
    return { isValid: false, errors: z.flattenError(result.error).fieldErrors };
  }
  return { isValid: true, data: result.data };
};

export const validateCustomField = (
  fieldId: string,
  value: string | string[] | undefined,
  fields: Record<string, Field>
): string[] | undefined => {
  const field = fields[fieldId];
  if (!field) {
    return undefined;
  }

  const schema = createFieldSchema(field);
  const result = schema.safeParse(value);

  if (!result.success) {
    // Extract error messages from the ZodError's issues
    const messages = result.error.issues.map((issue) => issue.message);
    return messages.length > 0 ? messages : undefined;
  }
  return undefined;
};

export const validateCustomFields = (
  customFields: DynamicFieldValues | undefined,
  requiredFieldIds: string[],
  fields: Record<string, Field>
): Record<string, string[]> => {
  return requiredFieldIds.reduce<Record<string, string[]>>((acc, fieldId) => {
    const fieldErrors = validateCustomField(
      fieldId,
      customFields?.[fieldId],
      fields
    );
    if (fieldErrors && fieldErrors.length > 0) {
      return { ...acc, [fieldId]: fieldErrors };
    }
    return acc;
  }, {});
};

export const validateField = <K extends keyof DraftIssue | string>(
  field: K,
  value: unknown,
  fields: Record<string, Field> = {}
): string[] | undefined => {
  // Check draft issue schema fields first (preserves backward compatibility)
  if (field in draftIssueSchema.shape) {
    const fieldSchema = draftIssueSchema.pick({
      [field as keyof DraftIssue]: true,
    });
    const result = fieldSchema.safeParse({ [field]: value });
    if (!result.success) {
      return z.flattenError(result.error).fieldErrors[
        field as keyof DraftIssue
      ];
    }
    return undefined;
  }

  // Then check dynamic custom fields
  if (field in fields) {
    return validateCustomField(
      field as string,
      value as string | string[] | undefined,
      fields
    );
  }

  return undefined;
};

export const validateDraftIssue = (
  draftIssue: ValidatableDraftIssue,
  fields: Record<string, Field>
): boolean => {
  const { area, department, category, description, attachments, customFields } =
    draftIssue;

  // Validate category selection
  if (
    !categoryValidationSchema.safeParse({ area, department, category }).success
  ) {
    return false;
  }

  // Validate description
  if (!descriptionSchema.safeParse(description).success) {
    return false;
  }

  // Validate attachments (always optional)
  if (!attachmentArraySchema.safeParse(attachments).success) {
    return false;
  }

  // Get dynamic fields from category, or from department for direct-to-summary flows
  const activeFields = category?.fields ?? department?.fields ?? [];
  const formFields = getFormFields(
    activeFields.map((f) => f.key),
    fields
  );
  const dynamicFieldIds = Object.keys(fields);
  const dynamicFields = formFields.filter(
    (field) =>
      field !== 'description' &&
      field !== 'attachments' &&
      dynamicFieldIds.includes(field)
  );

  // Validate all required custom fields
  const requiredFieldIds = dynamicFields.filter((fieldId) => {
    return fields[fieldId]?.isRequired;
  });

  const customFieldErrors = validateCustomFields(
    customFields,
    requiredFieldIds,
    fields
  );

  if (Object.keys(customFieldErrors).length > 0) {
    return false;
  }

  // Validate optional fields that have values (values must still be valid)
  const optionalFieldsWithValues = dynamicFields.filter((fieldId) => {
    const value = customFields?.[fieldId];
    const hasValue = Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== '';
    return !fields[fieldId]?.isRequired && hasValue;
  });

  const optionalFieldErrors = validateCustomFields(
    customFields,
    optionalFieldsWithValues,
    fields
  );
  if (Object.keys(optionalFieldErrors).length > 0) {
    return false;
  }

  return true;
};

export const validateDraftIssueAndStore = (
  draftIssue: ReturnType<typeof selectDraftIssueWithStore>,
  fields: Record<string, Field>
) => {
  return (
    storeValidationSchema.safeParse(draftIssue.store).success &&
    validateDraftIssue(draftIssue, fields)
  );
};
